import { useMemo, useState } from "react";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const fmtKM = (n) => {
    const v = Math.round((n || 0) * 100) / 100;
    const opts = (v % 1 === 0)
        ? { maximumFractionDigits: 0 }
        : { minimumFractionDigits: 2, maximumFractionDigits: 2 };
    return new Intl.NumberFormat("bs-BA", opts).format(v) + " KM";
};

const uid = () => Math.random().toString(36).slice(2, 10);

export default function QuoteBuilder({ pricing, modelImages, initial }) {
    const [customer, setCustomer] = useState(initial?.customer || { name: "", address: "", phone: "", email: "" });
    const [modelId, setModelId] = useState(initial?.model_id || "");

    // Fence segments: array of {key, len, hei}
    const [fences, setFences] = useState(() => {
        if (initial?.fences && initial.fences.length > 0) {
            return initial.fences.map(f => ({ key: uid(), len: String(f.length_m || ""), hei: String(f.height_m || "") }));
        }
        if (initial?.length_m && initial?.height_m) {
            return [{ key: uid(), len: String(initial.length_m), hei: String(initial.height_m) }];
        }
        return [];
    });

    // Gates: array of {key, id, len, hei, withSurcharge}
    const [gates, setGates] = useState(() =>
        (initial?.gates || []).map(g => ({
            key: uid(), id: g.id, len: String(g.length_m || ""), hei: String(g.height_m || ""),
            withSurcharge: !!g.with_surcharge,
        }))
    );

    // Motors: array of {key, id, qty}
    const [motors, setMotors] = useState(() =>
        (initial?.motors || []).map(m => ({ key: uid(), id: m.id, qty: String(m.quantity || 1) }))
    );

    const [accessoryId, setAccessoryId] = useState(initial?.accessory_id || "");
    const [installation, setInstallation] = useState(initial?.installation_price ? String(initial.installation_price) : "");
    const [submitting, setSubmitting] = useState(false);

    const selectedModel = pricing.models.find(m => m.id === modelId);
    const selectedAccessory = pricing.accessories.find(a => a.id === accessoryId);

    // Per-segment fence calc
    const fenceLines = useMemo(() => {
        return fences.map(f => {
            const l = parseFloat(f.len); const h = parseFloat(f.hei);
            const area = (!isNaN(l) && !isNaN(h) && l > 0 && h > 0) ? l * h : 0;
            const price = selectedModel ? selectedModel.price_per_m2 : 0;
            return { ...f, area, total: area * price };
        });
    }, [fences, selectedModel]);

    const area = useMemo(() => fenceLines.reduce((s, f) => s + f.area, 0), [fenceLines]);
    const fenceTotal = useMemo(() => fenceLines.reduce((s, f) => s + f.total, 0), [fenceLines]);

    const addFence = () => setFences(g => [...g, { key: uid(), len: "", hei: "" }]);
    const removeFence = (key) => setFences(g => g.filter(x => x.key !== key));
    const updateFence = (key, patch) => setFences(g => g.map(x => x.key === key ? { ...x, ...patch } : x));

    // Per-gate calc — price/m² comes from selected model
    // Special pjesacka logic: flat fee up to min, per-m² above
    const gateLines = useMemo(() => {
        return gates.map(g => {
            const def = pricing.gates.find(x => x.id === g.id);
            const pricePerM2 = selectedModel ? (selectedModel[`gate_${g.id}_per_m2`] || 0) : 0;
            if (!def) return { ...g, def: null, pricePerM2: 0, raw: 0, billable: 0, total: 0, surcharge: 0, isFlat: false };
            const l = parseFloat(g.len); const h = parseFloat(g.hei);
            const raw = (!isNaN(l) && !isNaN(h) && l > 0 && h > 0) ? l * h : 0;
            const minA = def.min_area_m2 || 0;

            let billable, baseTotal, isFlat, flatTier;
            if (g.id === "pjesacka" && minA > 0 && raw > 0) {
                if (raw <= minA) {
                    isFlat = true; flatTier = 1;
                    billable = raw;
                    baseTotal = Math.round(pricePerM2);
                } else if (raw <= 2.0) {
                    isFlat = true; flatTier = 2;
                    billable = raw;
                    baseTotal = Math.round(pricePerM2 * 1.2);
                } else {
                    isFlat = false;
                    billable = raw;
                    baseTotal = Math.round(raw * pricePerM2);
                }
            } else {
                isFlat = false;
                billable = Math.max(raw, minA);
                baseTotal = billable * pricePerM2;
            }
            const surcharge = (g.withSurcharge && def.surcharge_price > 0) ? def.surcharge_price : 0;
            return { ...g, def, pricePerM2, raw, billable, total: baseTotal, surcharge, isFlat, flatTier };
        });
    }, [gates, pricing.gates, selectedModel]);

    const gatesTotal = gateLines.reduce((s, l) => s + l.total + l.surcharge, 0);

    // Per-motor calc
    const motorLines = useMemo(() => {
        return motors.map(m => {
            const def = pricing.motors.find(x => x.id === m.id && x.enabled);
            const qty = Math.max(1, parseInt(m.qty || "1", 10));
            const lineTotal = def ? def.price * qty : 0;
            return { ...m, def, qty, total: lineTotal };
        });
    }, [motors, pricing.motors]);

    const motorsTotal = motorLines.reduce((s, l) => s + l.total, 0);

    const accTotal = useMemo(() => {
        if (!selectedAccessory) return 0;
        return selectedAccessory.price;
    }, [selectedAccessory]);

    const installTotal = useMemo(() => {
        const v = parseFloat(installation);
        return !isNaN(v) && v > 0 ? v : 0;
    }, [installation]);

    const grandTotal = fenceTotal + gatesTotal + motorsTotal + accTotal + installTotal;

    const addGate = (id) => setGates(g => [...g, { key: uid(), id, len: "", hei: "", withSurcharge: false }]);
    const removeGate = (key) => setGates(g => g.filter(x => x.key !== key));
    const updateGate = (key, patch) => setGates(g => g.map(x => x.key === key ? { ...x, ...patch } : x));

    const addMotor = (id) => setMotors(m => [...m, { key: uid(), id, qty: "1" }]);
    const removeMotor = (key) => setMotors(m => m.filter(x => x.key !== key));
    const updateMotor = (key, patch) => setMotors(m => m.map(x => x.key === key ? { ...x, ...patch } : x));

    const validate = () => {
        if (!customer.name.trim()) return "Unesite ime kupca (Korak 1)";
        if (!modelId) return "Odaberite model";
        if (area <= 0 && gateLines.length === 0) return "Dodajte segment ograde ili barem jednu kapiju";
        for (const f of fenceLines) {
            if (f.area <= 0) return "Unesite dužinu i visinu za svaki segment ograde";
        }
        for (const l of gateLines) {
            if (!l.def) return "Nepoznata kapija u listi";
            if (l.raw <= 0) return `Unesite dimenzije za "${l.def.name}"`;
        }
        for (const l of motorLines) {
            if (!l.def) return "Motor nije dostupan u sistemu";
        }
        return null;
    };

    const generate = async () => {
        const err = validate();
        if (err) { toast.error(err); return; }
        setSubmitting(true);
        try {
            const payload = {
                customer,
                model_id: modelId,
                length_m: 0,
                height_m: 0,
                price_per_m2: selectedModel.price_per_m2,
                fences: fenceLines.map(f => ({
                    length_m: parseFloat(f.len), height_m: parseFloat(f.hei),
                })),
                gates: gateLines.map(l => ({
                    id: l.def.id, name: l.def.name,
                    length_m: parseFloat(l.len), height_m: parseFloat(l.hei),
                    price_per_m2: l.pricePerM2, min_area_m2: l.def.min_area_m2 || 0,
                    with_surcharge: !!l.withSurcharge,
                    surcharge_name: l.def.surcharge_name || null,
                    surcharge_price: l.def.surcharge_price || 0,
                })),
                motors: motorLines.map(l => ({
                    id: l.def.id, name: l.def.name, price: l.def.price, quantity: l.qty,
                })),
            };
            if (selectedAccessory) {
                payload.accessory_id = selectedAccessory.id;
                payload.accessory_name = selectedAccessory.name;
                payload.accessory_price = accTotal;
            }
            if (installTotal > 0) payload.installation_price = installTotal;

            const r = await fetch(`${API}/api/quotes/generate`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify(payload),
            });
            if (!r.ok) throw new Error((await r.text()) || "Greška");
            const blob = await r.blob();
            const qn = r.headers.get("X-Quote-Number") || "ponuda";
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url; a.download = `${qn}.pdf`;
            document.body.appendChild(a); a.click(); a.remove();
            URL.revokeObjectURL(url);
            toast.success(`Ponuda ${qn} generisana`);
        } catch (e) {
            toast.error(e.message || "Greška");
        } finally { setSubmitting(false); }
    };

    return (
        <div className="grid lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
                {/* Step 1: Customer */}
                <Section title="1. Podaci kupca">
                    <div className="grid sm:grid-cols-2 gap-4">
                        <Field label="Ime i prezime *">
                            <input value={customer.name} onChange={e => setCustomer({ ...customer, name: e.target.value })}
                                data-testid="customer-name-input" className="form-input" placeholder="npr. Adnan Hadžić" />
                        </Field>
                        <Field label="Adresa">
                            <input value={customer.address} onChange={e => setCustomer({ ...customer, address: e.target.value })}
                                data-testid="customer-address-input" className="form-input" placeholder="Ulica i grad" />
                        </Field>
                        <Field label="Telefon">
                            <input value={customer.phone} onChange={e => setCustomer({ ...customer, phone: e.target.value })}
                                data-testid="customer-phone-input" className="form-input" placeholder="061 ..." />
                        </Field>
                        <Field label="Email">
                            <input type="email" value={customer.email} onChange={e => setCustomer({ ...customer, email: e.target.value })}
                                data-testid="customer-email-input" className="form-input" placeholder="ime@email.com" />
                        </Field>
                    </div>
                </Section>

                {/* Step 2: Model */}
                <Section title="2. Model ograde" subtitle="Izaberite jedan model">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {pricing.models.map(m => (
                            <button key={m.id} onClick={() => setModelId(m.id)} data-testid={`model-pick-${m.id}`}
                                className={`text-left bg-white border-2 transition-all p-3 ${
                                    modelId === m.id ? "border-[hsl(var(--accent))] shadow-sm" : "border-[hsl(var(--line))] hover:border-[hsl(var(--muted-ink))]"
                                }`}>
                                <div className="aspect-square bg-[hsl(var(--paper-2))] mb-2 overflow-hidden flex items-center justify-center">
                                    <img src={modelImages[m.id]} alt={m.name} className="w-full h-full object-contain" loading="lazy" />
                                </div>
                                <div className="font-bold text-sm">{m.name}</div>
                                <div className="text-xs text-[hsl(var(--muted-ink))] mt-0.5">{fmtKM(m.price_per_m2)}/m²</div>
                            </button>
                        ))}
                    </div>
                </Section>

                {/* Step 3: Fence segments (multi) */}
                <Section title="3. Ograda (opciono)" subtitle="Dodajte jedan ili više segmenata ograde — za različite visine ili razdvojene dionice">
                    {fenceLines.length > 0 && (
                        <div className="space-y-4 mb-5">
                            {fenceLines.map((f, idx) => (
                                <div key={f.key} className="bg-[hsl(var(--paper-2))] p-4 border border-[hsl(var(--line))]"
                                    data-testid={`fence-line-${f.key}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="font-bold">Segment {idx + 1}</div>
                                            {selectedModel && (
                                                <div className="text-xs text-[hsl(var(--muted-ink))]">{fmtKM(selectedModel.price_per_m2)}/m²</div>
                                            )}
                                        </div>
                                        <button onClick={() => removeFence(f.key)} data-testid={`fence-remove-${f.key}`}
                                            className="text-xs text-[hsl(var(--muted-ink))] hover:text-[hsl(var(--accent))]">
                                            ✕ Ukloni
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <Field label="Dužina (m)">
                                            <input type="number" step="0.01" min="0" value={f.len}
                                                onChange={e => updateFence(f.key, { len: e.target.value })}
                                                data-testid={`fence-length-${f.key}`} className="form-input" placeholder="npr. 12.5" />
                                        </Field>
                                        <Field label="Visina (m)">
                                            <input type="number" step="0.01" min="0" value={f.hei}
                                                onChange={e => updateFence(f.key, { hei: e.target.value })}
                                                data-testid={`fence-height-${f.key}`} className="form-input" placeholder="npr. 1.6" />
                                        </Field>
                                        <Field label="Površina">
                                            <div className="form-input bg-white font-bold flex items-center text-sm">
                                                {f.area > 0 ? `${f.area.toFixed(2)} m²` : "—"}
                                            </div>
                                        </Field>
                                        <Field label="Iznos">
                                            <div className="form-input bg-white font-bold flex items-center text-sm">
                                                {fmtKM(f.total)}
                                            </div>
                                        </Field>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button onClick={addFence} disabled={!selectedModel} data-testid="fence-add-btn"
                        className="px-4 py-2 border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))] text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[hsl(var(--line))]">
                        + Dodaj segment ograde
                    </button>
                    {!selectedModel && (
                        <span className="text-xs text-[hsl(var(--muted-ink))] ml-2">Prvo izaberite model</span>
                    )}
                </Section>

                {/* Step 4: Gates (multi) */}
                <Section title="4. Kapije (opciono)" subtitle="Dodajte koliko kapija želite — pješačka, dvokrilna, klizna">
                    {gateLines.length > 0 && (
                        <div className="space-y-4 mb-5">
                            {gateLines.map(l => (
                                <div key={l.key} className="bg-[hsl(var(--paper-2))] p-4 border border-[hsl(var(--line))]"
                                    data-testid={`gate-line-${l.key}`}>
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <div className="font-bold">{l.def?.name}</div>
                                            <div className="text-xs text-[hsl(var(--muted-ink))]">{fmtKM(l.pricePerM2)}/m²
                                                {l.def?.min_area_m2 > 0 && <span className="ml-2 text-[hsl(var(--accent))]">min {l.def.min_area_m2.toFixed(l.def.min_area_m2 % 1 === 0 ? 0 : 1)} m²</span>}
                                            </div>
                                        </div>
                                        <button onClick={() => removeGate(l.key)} data-testid={`gate-remove-${l.key}`}
                                            className="text-xs text-[hsl(var(--muted-ink))] hover:text-[hsl(var(--accent))]">
                                            ✕ Ukloni
                                        </button>
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                                        <Field label="Dužina (m)">
                                            <input type="number" step="0.01" min="0" value={l.len}
                                                onChange={e => updateGate(l.key, { len: e.target.value })}
                                                data-testid={`gate-length-${l.key}`} className="form-input" placeholder="npr. 2.5" />
                                        </Field>
                                        <Field label="Visina (m)">
                                            <input type="number" step="0.01" min="0" value={l.hei}
                                                onChange={e => updateGate(l.key, { hei: e.target.value })}
                                                data-testid={`gate-height-${l.key}`} className="form-input" placeholder="npr. 1.6" />
                                        </Field>
                                        <Field label="Obračunato">
                                            <div className="form-input bg-white font-bold flex items-center text-sm">
                                                {l.isFlat
                                                    ? (l.flatTier === 2 ? "paušal +20%" : "paušal")
                                                    : (l.billable > 0 ? `${l.billable.toFixed(2)} m²` : "—")}
                                            </div>
                                        </Field>
                                        <Field label="Iznos">
                                            <div className="form-input bg-white font-bold flex items-center text-sm">
                                                {fmtKM(l.total)}
                                            </div>
                                        </Field>
                                    </div>
                                    {/* Surcharge option (only when gate has surcharge_price > 0, e.g., klizna samonosiva) */}
                                    {l.def?.surcharge_price > 0 && (
                                        <label className="mt-3 flex items-center gap-2 text-sm cursor-pointer">
                                            <input type="checkbox" checked={l.withSurcharge}
                                                onChange={e => updateGate(l.key, { withSurcharge: e.target.checked })}
                                                data-testid={`gate-surcharge-${l.key}`}
                                                className="w-4 h-4 accent-[hsl(var(--accent))]" />
                                            <span>{l.def.surcharge_name || "Nadoplata"} <span className="text-[hsl(var(--muted-ink))]">(+{fmtKM(l.def.surcharge_price)})</span></span>
                                        </label>
                                    )}
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <span className="text-xs text-[hsl(var(--muted-ink))] flex items-center mr-2">
                            {selectedModel ? "Dodaj kapiju:" : "Prvo izaberite model — cijene kapija zavise od modela"}
                        </span>
                        {pricing.gates.map(g => (
                            <button key={g.id} onClick={() => addGate(g.id)} disabled={!selectedModel} data-testid={`gate-add-${g.id}`}
                                className="px-4 py-2 border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))] text-sm transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:border-[hsl(var(--line))]">
                                + {g.name}
                            </button>
                        ))}
                    </div>
                </Section>

                {/* Step 5: Motors (multi with qty) */}
                <Section title="5. Elektro motori (opciono)" subtitle="Možete dodati više motora i izabrati količinu">
                    {motorLines.length > 0 && (
                        <div className="space-y-3 mb-5">
                            {motorLines.map(l => (
                                <div key={l.key} className="bg-[hsl(var(--paper-2))] p-4 border border-[hsl(var(--line))] flex flex-wrap items-end gap-4"
                                    data-testid={`motor-line-${l.id}`}>
                                    <div className="flex-1 min-w-[180px]">
                                        <div className="font-bold">{l.def?.name}</div>
                                        <div className="text-xs text-[hsl(var(--muted-ink))]">{fmtKM(l.def?.price || 0)} / kom</div>
                                    </div>
                                    <Field label="Količina">
                                        <input type="number" min="1" max="20" step="1" value={l.qty}
                                            onChange={e => updateMotor(l.key, { qty: e.target.value })}
                                            data-testid={`motor-qty-${l.key}`} className="form-input w-24" />
                                    </Field>
                                    <div>
                                        <div className="text-xs text-[hsl(var(--muted-ink))] mb-1.5">Iznos</div>
                                        <div className="font-bold">{fmtKM(l.total)}</div>
                                    </div>
                                    <button onClick={() => removeMotor(l.key)} data-testid={`motor-remove-${l.key}`}
                                        className="text-xs text-[hsl(var(--muted-ink))] hover:text-[hsl(var(--accent))] self-start">
                                        ✕ Ukloni
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <div className="flex flex-wrap gap-2">
                        <span className="text-xs text-[hsl(var(--muted-ink))] flex items-center mr-2">Dodaj motor:</span>
                        {pricing.motors.filter(m => m.enabled).map(m => (
                            <button key={m.id} onClick={() => addMotor(m.id)} data-testid={`motor-add-${m.id}`}
                                className="px-4 py-2 border border-[hsl(var(--line))] hover:border-[hsl(var(--ink))] text-sm transition-colors">
                                + {m.name}
                            </button>
                        ))}
                    </div>
                </Section>

                {/* Step 6: Accessories */}
                <Section title="6. Pribor (opciono)">
                    <div className="grid sm:grid-cols-3 gap-3">
                        <OptionCard selected={accessoryId === ""} onClick={() => setAccessoryId("")}
                            testid="acc-none" label="Bez pribora" />
                        {pricing.accessories.map(a => (
                            <OptionCard key={a.id} selected={accessoryId === a.id} onClick={() => setAccessoryId(a.id)}
                                testid={`acc-${a.id}`} label={a.name} price={fmtKM(a.price)} />
                        ))}
                    </div>
                </Section>

                {/* Step 7: Installation */}
                <Section title="7. Montaža (opciono)">
                    <Field label="Cijena montaže (KM)">
                        <input type="number" step="0.01" min="0" value={installation}
                            onChange={e => setInstallation(e.target.value)} data-testid="installation-input"
                            className="form-input max-w-xs" placeholder="Ostavite prazno ako nije uključena" />
                    </Field>
                </Section>
            </div>

            {/* Summary */}
            <aside className="lg:col-span-1">
                <div className="bg-white border border-[hsl(var(--line))] sticky top-6">
                    <div className="p-5 border-b border-[hsl(var(--line))]">
                        <div className="text-[10px] tracking-widest text-[hsl(var(--muted-ink))]">PREGLED PONUDE</div>
                        <h3 className="text-xl font-bold mt-1">Informativna ponuda</h3>
                        {customer.name && <div className="text-xs text-[hsl(var(--muted-ink))] mt-2 truncate">Kupac: {customer.name}</div>}
                    </div>
                    <div className="p-5 space-y-3 text-sm">
                        {selectedModel && fenceLines.map((f, idx) => f.area > 0 && (
                            <SummaryRow key={f.key}
                                label={fenceLines.length > 1 ? `Ograda ${selectedModel.name} — segment ${idx + 1}` : `Ograda ${selectedModel.name}`}
                                subline={`${f.len} m × ${f.hei} m · ${f.area.toFixed(2)} m²`}
                                value={fmtKM(f.total)} testid={`sum-fence-${f.key}`} />
                        ))}
                        {gateLines.map(l => l.total > 0 && (
                            <div key={l.key}>
                                <SummaryRow label={l.def.name}
                                    subline={l.isFlat
                                        ? (l.flatTier === 1
                                            ? `paušal (do ${l.def.min_area_m2.toFixed(1)} m²) = ${fmtKM(l.total)}`
                                            : `paušal (1.5–2.0 m²) = ${fmtKM(l.pricePerM2)} +20%`)
                                        : `${l.billable.toFixed(2)} m² × ${fmtKM(l.pricePerM2)}/m²`}
                                    value={fmtKM(l.total)} testid={`sum-gate-${l.id}`} />
                                {l.surcharge > 0 && (
                                    <SummaryRow label={`↳ ${l.def.surcharge_name}`}
                                        value={fmtKM(l.surcharge)} testid={`sum-surcharge-${l.id}`} />
                                )}
                            </div>
                        ))}
                        {motorLines.map(l => l.total > 0 && (
                            <SummaryRow key={l.key} label={l.def.name}
                                subline={l.qty > 1 ? `${l.qty} × ${fmtKM(l.def.price)}` : null}
                                value={fmtKM(l.total)} testid={`sum-motor-${l.id}`} />
                        ))}
                        {selectedAccessory && <SummaryRow label={selectedAccessory.name} value={fmtKM(accTotal)} testid="sum-acc" />}
                        {installTotal > 0 && <SummaryRow label="Montaža" value={fmtKM(installTotal)} testid="sum-install" />}
                    </div>
                    <div className="p-5 bg-[hsl(var(--ink))] text-white">
                        <div className="text-xs opacity-80">UKUPNO (sa PDV-om)</div>
                        <div className="text-3xl font-bold mt-1" data-testid="grand-total">{fmtKM(grandTotal)}</div>
                    </div>
                    <div className="p-5">
                        <button onClick={generate} disabled={submitting} data-testid="generate-quote-btn"
                            className="w-full bg-[hsl(var(--accent))] text-white py-3.5 font-bold hover:opacity-90 disabled:opacity-50 transition-opacity">
                            {submitting ? "Generisanje…" : "Generiši PDF ponudu"}
                        </button>
                        <p className="text-[10px] text-[hsl(var(--muted-ink))] mt-3 text-center">
                            Sve cijene su sa uračunatim PDV-om. Vrijedi 15 dana.
                        </p>
                    </div>
                </div>
            </aside>
        </div>
    );
}

function Section({ title, subtitle, children }) {
    return (
        <div className="bg-white border border-[hsl(var(--line))] p-6">
            <div className="mb-5">
                <h2 className="text-lg font-bold">{title}</h2>
                {subtitle && <p className="text-xs text-[hsl(var(--muted-ink))] mt-1">{subtitle}</p>}
            </div>
            {children}
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="text-xs text-[hsl(var(--muted-ink))] block mb-1.5">{label}</span>
            {children}
        </label>
    );
}

function OptionCard({ selected, onClick, label, price, testid }) {
    return (
        <button onClick={onClick} data-testid={testid}
            className={`text-left p-3 border-2 transition-all ${
                selected ? "border-[hsl(var(--accent))] bg-[hsl(var(--paper-2))]" : "border-[hsl(var(--line))] hover:border-[hsl(var(--muted-ink))]"
            }`}>
            <div className="font-medium text-sm">{label}</div>
            {price && <div className="text-xs text-[hsl(var(--muted-ink))] mt-1">{price}</div>}
        </button>
    );
}

function SummaryRow({ label, subline, value, testid }) {
    return (
        <div className="flex justify-between items-start gap-3" data-testid={testid}>
            <div className="min-w-0 flex-1">
                <div className="truncate">{label}</div>
                {subline && <div className="text-[10px] text-[hsl(var(--muted-ink))] mt-0.5">{subline}</div>}
            </div>
            <div className="font-medium whitespace-nowrap">{value}</div>
        </div>
    );
}
